import { useTheme } from '@/hooks/useTheme';

const services = [
  {
    icon: 'ri-chat-voice-line',
    title: 'AI Intake & Communication Systems',
    description: 'Capture, qualify, and respond to every inbound lead instantly across voice, chat, and messaging channels. Never miss a conversation—day or night.',
    accent: 'cyan',
    imageUrl: 'https://public.readdy.ai/ai/img_res/12f34d26fbfc09d5d2f3c20b0fa1346d.jpg',
    stat: '< 2s',
    statLabel: 'avg. response time',
  },
  {
    icon: 'ri-flow-chart',
    title: 'Workflow Automation Infrastructure',
    description: 'Replace manual processes with structured workflows that guarantee consistency, speed, and accuracy across every touchpoint in your business.',
    accent: 'violet',
    imageUrl: 'https://readdy.ai/api/search-image?query=workflow%20automation%20visualization%20connected%20process%20nodes%20and%20flow%20paths%20glowing%20violet%20and%20purple%20light%20on%20dark%20background%20enterprise%20automation%20software%20interface%20depicting%20business%20process%20optimization%20clean%20and%20minimal%20design&width=720&height=420&seq=service-workflow-1&orientation=landscape',
    stat: '87%',
    statLabel: 'reduction in manual tasks',
  },
  {
    icon: 'ri-robot-2-line',
    title: 'AI Agent Deployment',
    description: 'Deploy intelligent agents that handle communication, follow-ups, and task execution with zero human delay—operating at machine speed, around the clock.',
    accent: 'cyan',
    imageUrl: 'https://readdy.ai/api/search-image?query=AI%20agent%20deployment%20visualization%20abstract%20humanoid%20intelligence%20core%20with%20circuit%20patterns%20glowing%20cyan%20neural%20connections%20dark%20background%20futuristic%20minimal%20design%20depicting%20autonomous%20task%20execution%20enterprise%20grade&width=720&height=420&seq=service-agent-1&orientation=landscape',
    stat: '24/7',
    statLabel: 'autonomous operation',
  },
  {
    icon: 'ri-database-2-line',
    title: 'CRM & Data Architecture',
    description: 'Centralize your entire operation into a single system of control. Full visibility across every pipeline, team, and data point—in real time.',
    accent: 'violet',
    imageUrl: 'https://readdy.ai/api/search-image?query=CRM%20data%20architecture%20visualization%20glowing%20database%20nodes%20connected%20by%20data%20streams%20enterprise%20control%20center%20dark%20navy%20background%20violet%20accent%20lights%20clean%20minimal%20data%20infrastructure%20design%20professional%20high%20tech&width=720&height=420&seq=service-crm-1&orientation=landscape',
    stat: '360°',
    statLabel: 'operational visibility',
  },
];

export default function CoreServicesSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="services" className="py-28 transition-colors duration-500" style={{ background: isDark ? '#0E1116' : '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className={`w-8 h-px ${isDark ? 'bg-cyan-400/50' : 'bg-blue-500/50'}`}></div>
            <span className={`text-xs tracking-widest uppercase font-medium ${isDark ? 'text-cyan-400' : 'text-blue-500'}`}>Core Services</span>
            <div className={`w-8 h-px ${isDark ? 'bg-cyan-400/50' : 'bg-blue-500/50'}`}></div>
          </div>
          <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6 ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
            Systems That Replace{' '}
            <span className="text-gradient-cyan">Friction</span>{' '}
            With Flow
          </h2>
          <p className={`text-lg max-w-xl mx-auto leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Four core systems. Each purpose-built to eliminate inefficiency and restore operational clarity.
          </p>
        </div>

        {/* Alternating service showcases */}
        <div className="flex flex-col gap-8">
          {services.map((service, idx) => {
            const isEven = idx % 2 === 0;
            return (
              <div
                key={idx}
                className={`group relative rounded-2xl overflow-hidden border transition-all duration-500 ${
                  isDark
                    ? 'border-white/6 hover:border-white/12'
                    : 'border-black/6 hover:border-black/12 shadow-sm hover:shadow-lg'
                }`}
                style={{ background: isDark ? '#111827' : '#F5F5F7' }}
              >
                <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} min-h-[380px]`}>
                  {/* Image */}
                  <div className="flex-1 relative overflow-hidden" style={{ minHeight: '260px' }}>
                    <img
                      src={service.imageUrl}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    {/* Overlay */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background: isDark
                          ? (isEven ? 'linear-gradient(90deg, transparent 60%, #111827 100%)' : 'linear-gradient(270deg, transparent 60%, #111827 100%)')
                          : (isEven ? 'linear-gradient(90deg, transparent 60%, #F5F5F7 100%)' : 'linear-gradient(270deg, transparent 60%, #F5F5F7 100%)'),
                      }}
                    />
                    {/* Stat badge */}
                    <div className="absolute bottom-6 left-6 lg:hidden">
                      <div className={`px-4 py-2 rounded-xl border backdrop-blur-md ${
                        service.accent === 'cyan'
                          ? isDark ? 'border-cyan-400/25 bg-[#0E1116]/80' : 'border-blue-500/25 bg-white/90'
                          : isDark ? 'border-violet-400/25 bg-[#0E1116]/80' : 'border-purple-500/25 bg-white/90'
                      }`}>
                        <p className={`text-2xl font-bold leading-none ${
                          service.accent === 'cyan' ? (isDark ? 'text-cyan-400' : 'text-blue-500') : (isDark ? 'text-violet-400' : 'text-purple-500')
                        }`}>
                          {service.stat}
                        </p>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{service.statLabel}</p>
                      </div>
                    </div>
                  </div>

                  {/* Text content */}
                  <div className="flex-none lg:w-[440px] flex flex-col justify-center p-8 lg:p-12">
                    {/* Number */}
                    <span className={`text-xs font-medium tracking-widest mb-6 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                      {String(idx + 1).padStart(2, '0')} —
                    </span>

                    {/* Icon */}
                    <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-6 ${
                      service.accent === 'cyan'
                        ? isDark ? 'bg-cyan-400/10 border border-cyan-400/20' : 'bg-blue-500/10 border border-blue-500/20'
                        : isDark ? 'bg-violet-400/10 border border-violet-400/20' : 'bg-purple-500/10 border border-purple-500/20'
                    }`}>
                      <i className={`${service.icon} text-xl ${
                        service.accent === 'cyan' ? (isDark ? 'text-cyan-400' : 'text-blue-500') : (isDark ? 'text-violet-400' : 'text-purple-500')
                      }`}></i>
                    </div>

                    {/* Title */}
                    <h3 className={`text-2xl font-bold tracking-tight leading-snug mb-4 ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className={`text-sm leading-relaxed mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {service.description}
                    </p>

                    {/* Stat (desktop) */}
                    <div className="hidden lg:flex items-end gap-3">
                      <p className={`text-4xl font-bold leading-none ${
                        service.accent === 'cyan' ? (isDark ? 'text-cyan-400' : 'text-blue-500') : (isDark ? 'text-violet-400' : 'text-purple-500')
                      }`}>
                        {service.stat}
                      </p>
                      <p className={`text-sm mb-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{service.statLabel}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}